import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import api, { setToken, getToken } from "@/lib/api";

const Ctx = createContext(null);
export const useApp = () => useContext(Ctx);

const LOCATION_KEY = "ll_location_id";
const BIZ_KEY = "ll_biz_id";

export function AppProvider({ children }) {
    const navigate = useNavigate();
    const [user, setUser] = useState(null); // null = unknown, false = anon
    const [businesses, setBusinesses] = useState([]);
    const [currentBusiness, setCurrentBusiness] = useState(null);
    const [currentLocationId, setCurrentLocationId] = useState(
        () => localStorage.getItem(LOCATION_KEY) || null,
    );
    const [bootstrapping, setBootstrapping] = useState(true);
    const [featureFlags, setFeatureFlags] = useState({});
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    const loadFeatureFlags = useCallback(async () => {
        try {
            const r = await api.features();
            setFeatureFlags(r.modules || {});
            setIsSuperAdmin(!!r.is_super_admin);
        } catch {
            setFeatureFlags({});
        }
    }, []);

    const loadBusinesses = useCallback(async () => {
        const { businesses: b } = await api.businesses();
        setBusinesses(b || []);
        const remembered = localStorage.getItem(BIZ_KEY);
        const pick =
            b.find((x) => x.id === remembered) || (b.length ? b[0] : null);
        setCurrentBusiness(pick);
        if (pick) localStorage.setItem(BIZ_KEY, pick.id);
        return pick;
    }, []);

    const bootstrap = useCallback(async () => {
        // If OAuth is landing, skip the /auth/me check.
        if (window.location.hash?.includes("session_id=")) {
            setBootstrapping(false);
            return;
        }
        if (!getToken()) {
            setUser(false);
            setBootstrapping(false);
            return;
        }
        try {
            const { user: u } = await api.me();
            setUser(u);
            await loadBusinesses();
            await loadFeatureFlags();
        } catch {
            setToken(null);
            setUser(false);
        } finally {
            setBootstrapping(false);
        }
    }, [loadBusinesses, loadFeatureFlags]);

    useEffect(() => {
        bootstrap();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = async (email, password) => {
        const { user: u, session_token } = await api.login(email, password);
        setToken(session_token);
        setUser(u);
        await loadFeatureFlags();
        if (u.role === "super_admin") {
            navigate("/admin");
            return;
        }
        const pick = await loadBusinesses();
        navigate(pick ? "/dashboard" : "/onboarding");
    };

    const finishGoogle = async (session_id) => {
        const { user: u, session_token } = await api.googleExchange(session_id);
        setToken(session_token);
        setUser(u);
        await loadBusinesses();
        await loadFeatureFlags();
    };

    const register = async (payload) => {
        const { user: u, session_token } = await api.register(payload);
        setToken(session_token);
        setUser(u);
        await loadBusinesses();
        await loadFeatureFlags();
        navigate("/onboarding");
    };

    const logout = async () => {
        try {
            await api.logout();
        } catch {}
        setToken(null);
        setUser(false);
        setBusinesses([]);
        setCurrentBusiness(null);
        localStorage.removeItem(BIZ_KEY);
        localStorage.removeItem(LOCATION_KEY);
        navigate("/login");
    };

    const switchBusiness = (id) => {
        const b = businesses.find((x) => x.id === id);
        if (!b) return;
        setCurrentBusiness(b);
        setCurrentLocationId(null);
        localStorage.setItem(BIZ_KEY, id);
        localStorage.removeItem(LOCATION_KEY);
    };

    const switchLocation = (id) => {
        setCurrentLocationId(id || null);
        if (id) localStorage.setItem(LOCATION_KEY, id);
        else localStorage.removeItem(LOCATION_KEY);
    };

    return (
        <Ctx.Provider
            value={{
                user,
                setUser,
                businesses,
                currentBusiness,
                currentLocationId,
                switchBusiness,
                switchLocation,
                login,
                register,
                logout,
                finishGoogle,
                bootstrapping,
                loadBusinesses,
                featureFlags,
                isSuperAdmin,
                loadFeatureFlags,
            }}
        >
            {children}
        </Ctx.Provider>
    );
}
